'use client';

import type { FC } from 'react';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { createTranslations } from '@/i18n/types';
import { useGameStatsQuery } from '../queries/useGameStatsQuery';
import { useGameStatsSubscription } from '../hooks/useGameStatsSubscription';

const translations = createTranslations({
  en: {
    caseOpenings: 'Cases',
    battles: 'Battles',
    contracts: 'Contracts',
    upgrades: 'Upgrades',
  },
  ru: {
    caseOpenings: 'Кейсов',
    battles: 'Баттлов',
    contracts: 'Контрактов',
    upgrades: 'Апгрейдов',
  },
  uz: {
    caseOpenings: 'Keyslar',
    battles: 'Janglar',
    contracts: 'Kontraktlar',
    upgrades: 'Apgreydlar',
  },
  fa: {
    caseOpenings: 'کیس‌ها',
    battles: 'نبردها',
    contracts: 'قراردادها',
    upgrades: 'ارتقاها',
  },
  pt: {
    caseOpenings: 'Caixas',
    battles: 'Batalhas',
    contracts: 'Contratos',
    upgrades: 'Upgrades',
  },
  tr: {
    caseOpenings: 'Kasa',
    battles: 'Savaş',
    contracts: 'Kontrat',
    upgrades: 'Upgrade',
  },
  uk: {
    caseOpenings: 'Кейсів',
    battles: 'Батлів',
    contracts: 'Контрактів',
    upgrades: 'Апгрейдів',
  },
  de: {
    caseOpenings: 'Cases',
    battles: 'Battles',
    contracts: 'Contracts',
    upgrades: 'Upgrades',
  },
  es: {
    caseOpenings: 'Cajas',
    battles: 'Battles',
    contracts: 'Contratos',
    upgrades: 'Upgrades',
  },
  pl: {
    caseOpenings: 'Skrzynek',
    battles: 'Battle',
    contracts: 'Kontraktów',
    upgrades: 'Upgrade’ów',
  },
});

const formatNumber = (n: number): string => new Intl.NumberFormat('ru-RU').format(n);

interface StatCellProps {
  label: string;
  value: number | undefined;
  accent: string;
}

const StatCell: FC<StatCellProps> = ({ label, value, accent }) => (
  <div className="flex flex-col items-center sm:items-start gap-1 p-4 sm:p-5 rounded-2xl border border-white/[0.04] bg-white/[0.02]">
    <span
      className="text-3xl sm:text-4xl font-bold tabular-nums tracking-tight"
      style={{
        background: accent,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      }}
    >
      {value != null ? formatNumber(value) : '—'}
    </span>
    <span className="text-xs sm:text-sm text-white/50 uppercase tracking-wider text-center sm:text-left">
      {label}
    </span>
  </div>
);

export const GlobalStatsBlock: FC = () => {
  const { t } = useLocalTranslation(translations);
  const { data } = useGameStatsQuery();
  useGameStatsSubscription();

  return (
    <section className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
      <StatCell
        label={t('caseOpenings')}
        value={data?.caseOpenings}
        accent="linear-gradient(90deg,#fde68a 0%,#f59e0b 100%)"
      />
      <StatCell
        label={t('battles')}
        value={data?.battles}
        accent="linear-gradient(90deg,#fca5a5 0%,#ef4444 100%)"
      />
      <StatCell
        label={t('contracts')}
        value={data?.contracts}
        accent="linear-gradient(90deg,#c4b5fd 0%,#a855f7 100%)"
      />
      <StatCell
        label={t('upgrades')}
        value={data?.upgrades}
        accent="linear-gradient(90deg,#86efac 0%,#22c55e 100%)"
      />
    </section>
  );
};
